import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { CustomAgentGateway } from 'src/app/application/gateway/CustomAgentGateway';
import { DocumentServiceGateway } from 'src/app/application/gateway/DocumentServiceGateway';
import { ImportOrderDocumentGateway } from 'src/app/application/gateway/ImportOrderDocumentGateway';
import { ImportOrderGateway } from 'src/app/application/gateway/ImportOrderGateway';
import { OperationDocumentTypeGateway } from 'src/app/application/gateway/OperationDocumentTypeGateway';
import { CustomAgentReview } from 'src/app/domain/models/CustomAgentReview';
import { ChatMessage, ImportOrderDocument, ImportOrderReviewMessageCreate, UploadImportOrderDocumentRequest } from 'src/app/domain/models/ImportOrder';
import { OperationDocumentType } from 'src/app/domain/models/Operation';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';

@Component({
  selector: 'app-custom-agent-origin-review',
  templateUrl: './custom-agent-origin-review.component.html',
  styleUrls: ['./custom-agent-origin-review.component.css'],
  providers: [ConfirmationService]
})
export class CustomAgentOriginReviewComponent implements OnInit {

  breadcrumbItems: MenuItem[] = []
  breadcrumbHomeItem: MenuItem = {}

  reviewId: string | undefined
  review: CustomAgentReview | undefined
  documents: ImportOrderDocument[] = []

  loading: boolean = false
  success: boolean = false
  loadingMessages: boolean = false

  visibleMessageDialog = false
  messages: ChatMessage[] = [];

  documentTypeId: string = ""
  operationDocumentTypes: OperationDocumentType[] = []


  profileUser: BasicUser | undefined


  navFrom: string = ''

  constructor(private _activatedRoute: ActivatedRoute,
    private _authService: AuthGateway,
    private _messageService: MessageService,
    public translate: TranslateService,
    private _importOrderDocumentService: ImportOrderDocumentGateway,
    private _documentService: DocumentServiceGateway,
    private _customAgentReviewService: CustomAgentGateway,
    private _importOrderService: ImportOrderGateway,
    private _confirmationService: ConfirmationService,
    private _operationDocumentTypeService: OperationDocumentTypeGateway){
      this._activatedRoute.queryParams.subscribe(queryParams => {
        this.navFrom = queryParams['navFrom'];
      })
    }

  ngOnInit(): void {

    this.profileUser = this._authService.getUser()

    let breadcrumbMenuItemLabel1 = "Origin Custom Agent Review Document"
    let breadcrumbHomeMenuItemLabel = 'Origin Custom Agent Pending Reviews'

    if(this.profileUser) {
      if(this.navFrom === "pending") {
        switch(this.profileUser.lang) {
          case 'en':
            breadcrumbMenuItemLabel1 = "Origin Custom Agent Review Document"
            breadcrumbHomeMenuItemLabel = 'Origin Custom Agent Pending Reviews'
          break
          case 'es':
            breadcrumbMenuItemLabel1 = 'Documento de revisión de Origin Custom Agent'
            breadcrumbHomeMenuItemLabel = 'Pendientes en Origen'
          break
        }
        this.breadcrumbItems = [{ label: breadcrumbMenuItemLabel1, disabled: true}]
        this.breadcrumbHomeItem = { label: breadcrumbHomeMenuItemLabel, routerLink: '/' + Routes.customAgentUserReviews };
      }
      else if(this.navFrom === "reviewed") {
        switch(this.profileUser.lang) {
          case 'en':
            breadcrumbMenuItemLabel1 = "Origin Custom Agent Review Document"
            breadcrumbHomeMenuItemLabel = 'Origin Forwarder Revieweds'
          break
          case 'es':
            breadcrumbMenuItemLabel1 = 'Documento de revisión de Origin Custom Agent'
            breadcrumbHomeMenuItemLabel = 'Revisadas en Origen'
          break
        }
        this.breadcrumbItems = [{ label: breadcrumbMenuItemLabel1, disabled: true}]
        this.breadcrumbHomeItem = { label: breadcrumbHomeMenuItemLabel, routerLink: '/' + Routes.customAgentUserOriginReviewed };
      }

    }
    this._activatedRoute.params.subscribe(params => {
      this.reviewId = params['reviewId']
    })

    this.loadExportDocumentType()
    this.loadReview()
  }

  loadExportDocumentType() {
    this.operationDocumentTypes =  []
    this._operationDocumentTypeService.getAll().subscribe({
      next: (v) => {
        this.operationDocumentTypes = v.map((item) => {
          return {
            ...item,
            description: this.translate.currentLang === 'es' ? item.nameEs : item.name
          }
        })
        this.loading = false
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOfferDocument.ManyRetrieveError') });
        console.log(e)
        this.loading = false
      },
      complete: () =>  {
        this.loading = false
        console.info('Operation Document types query successfully')
      }
    })
  }

  loadReview() {
    this.loading = true
    if(this.reviewId) {
      this._customAgentReviewService.getByReviewId(this.reviewId).subscribe({
        next: (v) => {
          console.log(v)
          this.review = v
          this.loadDocuments()
          this.loading = false
        },
        error: (e) => {
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: 'Could not retrieve the import order detail' });

          this.loading = false
          console.log(e)
        },
        complete: () =>  {
          this.loading = false
          console.info('Review query successfully')
        }
      })
    }
  }

  loadDocuments() {
    if (this.review)
    {
      this.loading = true
      this.documents = []
      this._importOrderDocumentService.getAllImportOrderDocuments(this.review.importOrderGuid, 1).subscribe({
        next: (v) => {
          console.log(v)
          this.documents = v
          this.loading = false
        },
        error: (e) => {
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: 'Could not retrieve the import order details' });

          this.loading = false
          console.log(e)
        },
        complete: () =>  {
          this.loading = false
          console.info('Documents query successfully')
        }
      })
    }
  }

  getImportOrderDetailDocument(doc: any) {
    this._documentService.getImportOrderDetailDocument(doc.guid).subscribe((resultBlob: any) => {

      const contentType = resultBlob.headers.get('Content-Type');
      const blob = new Blob([resultBlob.body], { type: contentType });
      const downloadURL = window.URL.createObjectURL(blob);

      if (contentType === 'application/pdf') {
        window.open(downloadURL)
      } else {
        // Para otros tipos de archivos, forzar la descarga
        const anchor = window.document.createElement('a');
        anchor.href = downloadURL;
        anchor.download = doc.name;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }

      window.URL.revokeObjectURL(downloadURL);
    })
  }

  onUploadDocumentPdf(fileUpload: any) {
    if (this.review && this.documentTypeId)
    {
      this.loading = true
      let request: UploadImportOrderDocumentRequest = {
        guid: this.review.importOrderGuid,
        document: fileUpload.files[0],
        documentType: 1, // Custom Agent
        operationDocumentTypeId: this.documentTypeId
      }

      this._importOrderDocumentService.uploadImportOrderDocument(request).subscribe({
        next: (v) => {
          this.loadReview()
          this.loadDocuments()
          this.documentTypeId = ''
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('CustomAgentPage.Messages.SuccessUploadDocument') })
        },
        error: (e) => {
          console.log(e)
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('CustomAgentPage.Messages.ErrorUploadDocument') })
        },
        complete: () =>  {
          this.loading = false
          console.info('Export Invoice uploaded successfully')
        }
      })
      fileUpload.clear()
    }
  }

  deleteDocument (doc: any){
    this._confirmationService.confirm({
      message: this.translate.instant('CustomAgentPage.CustomAgentUserOriginReview.DialogDocument.Title'),
      header: this.translate.instant('CustomAgentPage.CustomAgentUserOriginReview.DialogDocument.Message'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.loading = true
        this._importOrderDocumentService.deleteImportOrderDocument(doc.guid).subscribe(
          {
            next: () => {
              this._messageService.add({ severity: 'success', summary: this.translate.instant('MessageService.Summary.SuccessAction'), detail: this.translate.instant('CustomAgentPage.Messages.SuccessDeleteDocument') });
              this.loadDocuments()
            },
            error: (e) => {
              console.log(e)
              this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('CustomAgentPage.Messages.ErrorDeleteDocument') });
              this.loading = false
            },
            complete: () => {
              this.loading = false
            }
          }
        )
      },
      reject: (type: any) => {}
    })
  }

  openMessageDialog() {
    this.visibleMessageDialog = true
    this.loadMessages()
  }

  getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(formGroup, this.translate, fieldName)
  }

  loadMessages() {
    if (this.review?.importOrderGuid) {
      this.loadingMessages = true
      this._importOrderService.getReviewMessages(this.review?.importOrderGuid).subscribe({
        next: (v) => {
          this.messages = v.map((item) => {
            return {
              sender: item.profile.contactEmail,
              message: item.message,
              created: item.created
            }
          })
          if (this.review) this.review.messages = 0
        },
        error: (e) => {
          console.log(e)
        },
        complete: () => console.info('Review Message query successfully')
      })
      this.loadingMessages = false
    }
  }

  onMessageSent(message: ChatMessage) {
    if (message.message) {
      this.loading = true;
      const newMessage: ImportOrderReviewMessageCreate = {
        importOrderId: this.review?.importOrderGuid,
        message: message.message
      }
      this._importOrderService.addReviewMessages(newMessage).subscribe({
        next: (v) => {
          this.loading = false;
          this.loadMessages();
        },
        error: (e) => {
          console.log(e)
          this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('ChatNotification.Messages.ErrorLoadMessages') });
        },
        complete: () => console.info('Review Message query successfully')
      })
    }
  }

  get messagePending(): string {
    if(this.review?.messages)
      return this.review?.messages > 0 ? this.review?.messages?.toString() : '';
    return ''
  }

  onSelectDocumentType(event: any) {
    this.documentTypeId = event.value
  }

}
